import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react'
import type { BoxTruckDriverLastLocation } from '@numeo/types-core'
import { readPerWindow, readPerWindowScoped, writePerWindow, writePerWindowScoped } from 'features/load-hub/utils/perWindowStorage'
import type { BoxTruckVerification } from '../services/boxTruckLoadsApi'
import { sanitizeBoxTruckSelectedDrivers } from '../utils/browserTabHandoff'
import { BOX_TRUCK_WORKSPACE_TTL_MS } from '../utils/boxTruckTabExpiration'

const STORAGE_KEY = 'box_truck_selected_drivers'

type BoxTruckDriversByTab = Record<string, BoxTruckVerification[]>

interface UseBoxTruckDriverSelectionsOptions {
    validKeys: string[]
    activeTabId: string
    importedDrivers?: BoxTruckVerification[]
    handoffRequested: boolean
    isolatedWindow: boolean
    persist: boolean
}

interface BoxTruckDriverSelectionsApi {
    driversByTab: BoxTruckDriversByTab
    setDriversByTab: Dispatch<SetStateAction<BoxTruckDriversByTab>>
    /** Apply an operator's saved location correction to every board's copy of that driver. */
    patchDriverLocation: (userId: string, lastLocation: BoxTruckDriverLastLocation) => void
}

const sanitizeSelections = (value: unknown, validKeys: Set<string>): BoxTruckDriversByTab => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return {}

    const selections: BoxTruckDriversByTab = {}
    for (const [key, drivers] of Object.entries(value)) {
        if (!validKeys.has(key)) continue
        const sanitized = sanitizeBoxTruckSelectedDrivers(drivers)
        if (sanitized.length > 0) selections[key] = sanitized
    }
    return selections
}

const readPersisted = (validKeys: string[], scopedOnly: boolean): BoxTruckDriversByTab => {
    try {
        const raw = scopedOnly ? readPerWindowScoped(STORAGE_KEY) : readPerWindow(STORAGE_KEY)
        return raw ? sanitizeSelections(JSON.parse(raw), new Set(validKeys)) : {}
    } catch {
        return {}
    }
}

const writePersisted = (selections: BoxTruckDriversByTab, validKeys: string[], scopedOnly: boolean): void => {
    const sanitized = sanitizeSelections(selections, new Set(validKeys))
    const write = scopedOnly ? writePerWindowScoped : writePerWindow
    write(STORAGE_KEY, JSON.stringify(sanitized), BOX_TRUCK_WORKSPACE_TTL_MS)
}

/**
 * Keeps each internal search tab's selected-driver context across reloads while preserving top-level browser
 * tab isolation. Only the scrubbed fields accepted by browser-tab handoffs are stored; verification evidence,
 * documents, banking data, HR data and Telegram details never enter this persistence record.
 */
export const useBoxTruckDriverSelections = ({
    validKeys,
    activeTabId,
    importedDrivers = [],
    handoffRequested,
    isolatedWindow,
    persist,
}: UseBoxTruckDriverSelectionsOptions): BoxTruckDriverSelectionsApi => {
    const [driversByTab, setDriversByTab] = useState<BoxTruckDriversByTab>(() => {
        const restored = handoffRequested ? {} : readPersisted(validKeys, isolatedWindow)
        const imported = sanitizeBoxTruckSelectedDrivers(importedDrivers)
        return imported.length > 0 ? { ...restored, [activeTabId]: imported } : restored
    })

    useEffect(() => {
        if (!persist) return
        writePersisted(driversByTab, validKeys, isolatedWindow)
    }, [driversByTab, isolatedWindow, persist, validKeys])

    /**
     * A selection is a SNAPSHOT of the driver's server record, persisted to localStorage — so an operator's
     * location correction left this store holding the PRE-EDIT fix until the driver was unpicked and
     * re-picked. That stale copy is what Nearby Drivers rebuilds a pinned row from once the corrected
     * position falls outside the radius and the geo query stops returning the driver, which reads to the
     * operator as their edit reverting to the previous location (and surviving a reload, because the store
     * is on disk). Patching lives HERE, where the store does, so every caller shares one implementation
     * instead of re-deriving the same update.
     *
     * Every board is patched, not just the active one: the same driver can be picked on several search tabs
     * and on Driver Bids, and a board left holding the old fix resurrects it the next time it is opened.
     *
     * COVERS the correction surfaces that render inside the loadboard — the "Search for driver" picker, the
     * load drawer's Nearby Drivers list, and the Search Vehicles drawer. It does NOT cover the My Drivers
     * page: that is a separate route with no access to this store, so a correction made there still leaves a
     * picked driver's snapshot stale until the loadboard's selection is re-picked. Closing that needs the
     * selection to stop carrying server-owned fields at all (persist ids, re-hydrate on mount) rather than
     * another caller wired into this patch.
     */
    const patchDriverLocation = useCallback((userId: string, lastLocation: BoxTruckDriverLastLocation) => {
        setDriversByTab((previous) => {
            let patched = false
            const next: BoxTruckDriversByTab = {}
            for (const [boardKey, drivers] of Object.entries(previous)) {
                next[boardKey] = drivers.map((driver) => {
                    if (driver.userId !== userId) return driver
                    patched = true
                    return { ...driver, lastLocation }
                })
            }
            // Identity is preserved when the driver isn't picked anywhere, so a correction made on a driver
            // nobody selected costs neither a re-render nor a localStorage rewrite.
            return patched ? next : previous
        })
    }, [])

    return { driversByTab, setDriversByTab, patchDriverLocation }
}
